import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { getStoredToken, setUnauthorizedHandler, storeToken } from "../api/session";

type AuthState = {
  token: string | null;
  setToken: (token: string | null) => void;
  isAuthenticated: boolean;
  logout: () => void;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getStoredToken());

  const setToken = useCallback((value: string | null) => {
    storeToken(value);
    setTokenState(value);
  }, []);

  const logout = useCallback(() => setToken(null), [setToken]);

  // Une réponse 401 de l'API vide la session ici : l'état React est mis à jour, donc le
  // garde AuthRequired redirige vers la connexion au rendu suivant. Vider seulement le
  // localStorage ne suffirait pas, l'état ne changerait pas.
  useEffect(() => {
    setUnauthorizedHandler(() => setToken(null));
    return () => setUnauthorizedHandler(null);
  }, [setToken]);

  const value = useMemo<AuthState>(
    () => ({ token, setToken, isAuthenticated: !!token, logout }),
    [token, setToken, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export { getStoredToken } from "../api/session";
